/* eslint-disable @typescript-eslint/no-explicit-any */
import { MessageType } from 'providers/RealTime/types';

import { getFirst } from 'utils/array';

import { LoadResult, LoadParams, OptionalArguments } from './types';
import { Loaders } from './definition';
import { createUseLoad, LoadOptions, UseEntityLoad } from './createHook';

type HookParams<
  TargetLoaders extends Record<string, Loaders<any>>,
  Realm extends keyof TargetLoaders,
  SubRealm extends keyof TargetLoaders[Realm],
  DefaultValue extends LoadResult<TargetLoaders, Realm, SubRealm> | undefined,
  SubType extends MessageType = MessageType,
> = OptionalArguments<
  LoadOptions<TargetLoaders, Realm, SubRealm, DefaultValue, SubType>,
  LoadParams<TargetLoaders, Realm, SubRealm>
>;

type HookLoaders<TargetLoaders extends Record<string, Loaders<any>>> = {
  [Realm in keyof TargetLoaders]: {
    [SubRealm in keyof TargetLoaders[Realm]]: {
      use<
        DefaultValue extends LoadResult<TargetLoaders, Realm, SubRealm> | undefined,
        SubType extends MessageType = MessageType,
      >(
        ...params: HookParams<TargetLoaders, Realm, SubRealm, DefaultValue, SubType>
      ): UseEntityLoad<TargetLoaders, Realm, SubRealm, DefaultValue>;

      useV2<
        DefaultValue extends LoadResult<TargetLoaders, Realm, SubRealm> | undefined,
        SubType extends MessageType = MessageType,
      >(
        ...params: HookParams<TargetLoaders, Realm, SubRealm, DefaultValue, SubType>
      ): [
        UseEntityLoad<TargetLoaders, Realm, SubRealm, DefaultValue>['data'],
        Omit<UseEntityLoad<TargetLoaders, Realm, SubRealm, DefaultValue>, 'data'>,
      ];
    };
  };
};

type UseLoadFn = ReturnType<typeof createUseLoad>;

function buildSubRealm<
  TargetLoaders extends Record<string, Loaders<any>>,
  Module extends keyof TargetLoaders,
  Target extends keyof TargetLoaders[Module],
>(realm: Module, subRealm: Target, useLoad: UseLoadFn): HookLoaders<TargetLoaders>[Module][Target] {
  return {
    use(...params: any[]) {
      // this is a perfectly fine call if used inside a component!
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useLoad(realm as string, subRealm, getFirst(params));
    },

    useV2(...params: any[]) {
      const { data, ...actions } = useLoad(realm as string, subRealm, getFirst(params));

      return [data, actions];
    },
  } as HookLoaders<TargetLoaders>[Module][Target];
}

function buildLoadRealm<
  TargetLoaders extends Record<string, Loaders<any>>,
  Module extends keyof TargetLoaders,
>(
  realm: Module,
  config: TargetLoaders[Module],
  useLoad: UseLoadFn,
): HookLoaders<TargetLoaders>[Module] {
  return Object.fromEntries(
    Object.keys(config).map((subRealmKey) => [
      subRealmKey,
      buildSubRealm(realm as any, subRealmKey as any, useLoad),
    ]),
  ) as unknown as HookLoaders<TargetLoaders>[Module];
}

export function createLoaderObject<TargetLoaders extends Record<string, Loaders<any>>>(
  entityLoaders: TargetLoaders,
  useLoad: UseLoadFn,
): HookLoaders<TargetLoaders> {
  return Object.fromEntries(
    Object.entries(entityLoaders).map(([realmKey, realm]) => [
      realmKey,
      buildLoadRealm(realmKey as keyof TargetLoaders, realm as any, useLoad),
    ]),
  ) as HookLoaders<TargetLoaders>;
}
